import { useEffect } from 'react';
import { publishUIMessage } from 'Common/notifications';
import { defineMessage, useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAvgBasketValue,
  getMarginValue,
  getOrdersCount,
  getRevenueValue,
} from 'Reports/selectors';
import {
  OrdersResponse,
  ProductSoldReportRecord,
  ReportFilterOptions,
  ReturnsResponse,
} from 'utils/apiClient/services/reports/types';
import {
  generateBestsellersReport,
  generateRevenueByPaymentReport,
  RevenueByPaymentReportRecord,
} from 'Reports/utils';
import { useApiClient } from 'utils/apiClient';
import { handleErrorNotification } from 'Common/utils';

export const useBasic = (options: ReportFilterOptions) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['reports', 'basic', options],
    queryFn: async () => {
      try {
        const data = await apiClient.Reports.loadBasic(options);
        queryClient.setQueryData(
          ['reports', 'cards', 'invoices'],
          getOrdersCount(data) ?? []
        );
        queryClient.setQueryData(
          ['reports', 'cards', 'margin'],
          getMarginValue(data) ?? []
        );
        queryClient.setQueryData(
          ['reports', 'cards', 'totalRevenue'],
          getRevenueValue(data) ?? []
        );
        queryClient.setQueryData(
          ['reports', 'cards', 'avgBasketValue'],
          getAvgBasketValue(getRevenueValue(data), getOrdersCount(data)) ?? 0
        );
        return data;
      } catch (err) {
        return null;
      }
    },
    enabled: options !== undefined,
    refetchOnWindowFocus: false,
  });
};

export const useIncomeTransactions = (params: ReportFilterOptions) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { data, error, isLoading } = useQuery({
    queryKey: ['reports', 'income-transactions', params],
    queryFn: async () => {
      try {
        return await apiClient.Reports.loadIncomeTransactions(params);
      } catch (err) {
        return null;
      }
    },
    enabled: params !== undefined,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data && !error) {
      queryClient.setQueryData(
        ['reports', 'revenue-payment'],
        generateRevenueByPaymentReport(data)
      );
    }
  }, [data, error, queryClient]);

  return { data, error, isLoading };
};
export const useOrders = (params: ReportFilterOptions) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['reports', 'orders', params],
    queryFn: () => apiClient.Reports.loadOrders(params),
    refetchOnWindowFocus: false,
    enabled: params !== undefined,
    select: (data: OrdersResponse) => data.data,
  });
};

export const useProductsSold = (params: ReportFilterOptions) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['reports', 'products-sold', params],
    queryFn: () => apiClient.Reports.loadProductsSold(params),
    enabled: params !== undefined,
    refetchOnWindowFocus: false,
  });
};

export const useReturns = (params: ReportFilterOptions) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['reports', 'returns', params],
    queryFn: async () => {
      try {
        const data = await apiClient.Reports.loadReturns(params);
        queryClient.setQueryData(['reports', 'latest-returns'], data);
        return data;
      } catch (err) {
        return null;
      }
    },
    select: (data: ReturnsResponse) => data.data,
    refetchOnWindowFocus: false,
    enabled: params !== undefined,
  });
};

export const useBestSellersReport = (params: ReportFilterOptions) => {
  const productsSoldQuery = useProductsSold(params);

  return {
    soldReports: productsSoldQuery.data,
    data:
      productsSoldQuery.data !== undefined
        ? generateBestsellersReport(
            productsSoldQuery.data as Array<ProductSoldReportRecord>
          ).slice(0, 5)
        : [],
    loading: productsSoldQuery.isLoading,
  };
};

export const useRevenueByPaymentReport = () => {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData(['reports', 'revenue-payment']);

  if (data !== undefined) {
    return data as Array<RevenueByPaymentReportRecord>;
  }
  return [];
};

export const useEmailTransactionsReport = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  return useMutation({
    mutationFn: apiClient.Reports.emailTransactionsReport,
    onSuccess: () => {
      publishUIMessage(
        'success',
        defineMessage({ defaultMessage: 'Mail sent successfully' })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useEmailBestSellersReport = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  return useMutation({
    mutationFn: apiClient.Reports.emailBestSellersReport,
    onSuccess: () => {
      publishUIMessage(
        'success',
        defineMessage({ defaultMessage: 'Mail sent successfully' })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};
